import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../hooks/useAuth';
import { isAdmin, isUser } from '../types';
import LoadingSpinner from './LoadingSpinner';
import BaseModal from './ui/BaseModal';
import { toast } from '../hooks/useNotification';
import { extractErrorMessage } from '../utils/errorHandling';
import CountrySelectModal from './modals/CountrySelectModal';
import { Country } from '../api/countries.api';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ProfileData {
  id: number;
  email: string;
  name?: string;
  companyName?: string;
  contactPerson?: string;
  phone?: string;
  countryCode?: string;
  role?: string;
  userType: string;
  emailNotificationsEnabled?: boolean;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  const { user, userType } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [changingPassword, setChangingPassword] = useState<boolean>(false);
  const [showPasswordForm, setShowPasswordForm] = useState<boolean>(false);
  const [hasPassword, setHasPassword] = useState<boolean>(false);
  const [checkingPassword, setCheckingPassword] = useState<boolean>(false);
  const [showCountryModal, setShowCountryModal] = useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);

  // Profile form data
  const [formData, setFormData] = useState<ProfileData>({
    id: 0,
    email: '',
    name: '',
    companyName: '',
    contactPerson: '',
    phone: '',
    countryCode: '',
    userType: '',
    emailNotificationsEnabled: true
  });

  // Password form data
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Fetch profile data from DB
  const fetchProfileData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/profile');
      const data = response?.data?.data || response?.data;

      if (data) {
        setFormData({
          id: data.id,
          email: data.email,
          name: data.name || '',
          companyName: data.company_name || data.companyName || '',
          contactPerson: data.contact_person || data.contactPerson || '',
          phone: data.phone || '',
          countryCode: data.country_code || data.countryCode || '',
          role: data.role || '',
          userType: userType || '',
          emailNotificationsEnabled: data.emailNotificationsEnabled ?? true
        });
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      // Fallback to auth user data
      if (isAdmin(user)) {
        setFormData({
          id: user!.id,
          email: user!.email,
          name: (user as any).name || '',
          userType: 'admin'
        });
      } else if (isUser(user)) {
        setFormData({
          id: user!.id,
          email: user!.email,
          companyName: user!.company_name || '',
          contactPerson: user!.contact_person || '',
          phone: user!.phone || '',
          countryCode: (user as any).country_code || '',
          userType: 'brand'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      // Fetch profile from DB
      fetchProfileData();
      // Check if user has password set
      checkHasPassword();
    }
  }, [isOpen, user, userType]);

  const checkHasPassword = async () => {
    setCheckingPassword(true);
    try {
      const response = await api.get('/profile/has-password');
      const data = response?.data as any;
      setHasPassword(data?.data?.hasPassword || data?.hasPassword || false);
    } catch (error) {
      console.error('Failed to check password status:', error);
      setHasPassword(false);
    } finally {
      setCheckingPassword(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const updateData: Record<string, string> = {};

      if (userType === 'admin') {
        if (formData.name) updateData.name = formData.name;
        updateData.emailNotificationsEnabled = String(formData.emailNotificationsEnabled);
      } else {
        if (formData.contactPerson) updateData.contactPerson = formData.contactPerson;
        if (formData.companyName) updateData.companyName = formData.companyName;
        updateData.phone = formData.phone || '';
        updateData.countryCode = formData.countryCode || '';
        // Include email update for brand users
        if (formData.email) updateData.email = formData.email;
      }

      await api.put('/profile', updateData);
      toast.success('Profile updated successfully');
      onClose();
      // Reload page to refresh user data in auth state
      window.location.reload();
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      toast.error(extractErrorMessage(error.response?.data?.error) || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    // If user has existing password, current password is required
    if (hasPassword && !passwordData.currentPassword) {
      toast.error('Current password is required');
      return;
    }

    setChangingPassword(true);
    try {
      const payload: Record<string, string> = {
        newPassword: passwordData.newPassword
      };
      // Only include currentPassword if user has existing password
      if (hasPassword) {
        payload.currentPassword = passwordData.currentPassword;
      }

      await api.put('/profile/password', payload);
      toast.success(hasPassword ? 'Password changed successfully' : 'Password registered successfully! You can now login with password.');
      setShowPasswordForm(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setHasPassword(true); // Update local state
    } catch (error: any) {
      console.error('Failed to change password:', error);
      toast.error(extractErrorMessage(error.response?.data?.error) || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <BaseModal isOpen={isOpen} onClose={onClose} title="Profile Settings" size="md">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Profile Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  {userType === 'admin' ? (
                    <>
                      <input
                        type="email"
                        value={formData.email}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
                    </>
                  ) : (
                    <>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-np-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">You will need to use this new email for your next login</p>
                    </>
                  )}
                </div>

                {userType === 'admin' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-np-500"
                      />
                    </div>

                    {/* Email Notifications Toggle */}
                    <div className="flex items-center justify-between py-3 border-t border-gray-200 mt-2">
                      <div>
                        <label className="text-sm font-medium text-gray-700">
                          Email Notifications
                        </label>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Receive email alerts for new sample requests in your assigned projects
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          emailNotificationsEnabled: !prev.emailNotificationsEnabled
                        }))}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-np-500 focus:ring-offset-2 ${
                          formData.emailNotificationsEnabled ? 'bg-np-500' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            formData.emailNotificationsEnabled ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Person
                      </label>
                      <input
                        type="text"
                        name="contactPerson"
                        value={formData.contactPerson || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-np-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-np-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone || ''}
                        onChange={handleInputChange}
                        placeholder="e.g., 010-1234-5678"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-np-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowCountryModal(true)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-left focus:outline-none focus:ring-2 focus:ring-np-500 hover:bg-gray-50 flex items-center justify-between"
                      >
                        <span className={formData.countryCode ? 'text-gray-900' : 'text-gray-400'}>
                          {selectedCountry
                            ? `${selectedCountry.name_en} (${selectedCountry.code})`
                            : formData.countryCode || 'Select country'}
                        </span>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </>
                )}

                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="btn-primary flex items-center"
                  >
                    {saving && <LoadingSpinner size="sm" color="white" className="mr-2" />}
                    Save Changes
                  </button>
                </div>
              </div>

              {/* Password Section (Admin and Brand users) */}
              <div className="border-t pt-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Password</h4>

                {checkingPassword ? (
                  <div className="flex items-center text-sm text-gray-500">
                    <LoadingSpinner size="sm" className="mr-2" />
                    Checking password status...
                  </div>
                ) : !showPasswordForm ? (
                  <div>
                    {hasPassword ? (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Password is set</span>
                        <button
                          onClick={() => setShowPasswordForm(true)}
                          className="text-np-600 hover:text-np-700 text-sm font-medium"
                        >
                          Change Password
                        </button>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                        <p className="text-sm text-blue-800 mb-3">
                          Register a password to enable password login. You can still use email verification to login.
                        </p>
                        <button
                          onClick={() => setShowPasswordForm(true)}
                          className="btn-primary text-sm"
                        >
                          Register Password
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {hasPassword && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Current Password
                        </label>
                        <input
                          type="password"
                          name="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-np-500"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {hasPassword ? 'New Password' : 'Password'}
                      </label>
                      <input
                        type="password"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-np-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">At least 8 characters</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-np-500"
                      />
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => {
                          setShowPasswordForm(false);
                          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        }}
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleChangePassword}
                        disabled={changingPassword || (hasPassword && !passwordData.currentPassword) || !passwordData.newPassword}
                        className="btn-primary flex items-center disabled:opacity-50"
                      >
                        {changingPassword && <LoadingSpinner size="sm" color="white" className="mr-2" />}
                        {hasPassword ? 'Change Password' : 'Register Password'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
      </BaseModal>

      {/* Country Select Modal — 중첩 modal 이므로 ProfileModal 밖 형제로 둔다(백드롭 계층 분리) */}
      <CountrySelectModal
        isOpen={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        onSelect={(country: Country) => {
          setSelectedCountry(country);
          setFormData(prev => ({ ...prev, countryCode: country.code }));
        }}
        selectedCode={formData.countryCode}
      />
    </>
  );
};

export default ProfileModal;
